import {
	getFormProps,
	getInputProps,
	type SubmissionResult,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { startAuthentication } from '@simplewebauthn/browser'
import { useOptimistic, useState, useTransition } from 'react'
import {
	data,
	Form,
	Link,
	redirect,
	useNavigate,
	useSearchParams,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { z } from 'zod'
import bannerImage from '#app/assets/akademie-banner.jpeg'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { CheckboxField, ErrorList, Field } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { ConfirmationEmailAccountEmail } from '#app/emails/confirmation-emailaccount-email.tsx'
import { prepareVerification } from '#app/routes/_auth+/verify.server.ts'
import { login, requireAnonymous } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getErrorMessage, useIsPending } from '#app/utils/misc.tsx'
import {
	EmailSchema,
	PasswordSchema,
	UsernameSchema,
} from '#app/utils/user-validation.ts'
import { type Route } from './+types/login.ts'
import { handleNewSession } from './login.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const LoginFormSchema = z.object({
	usernameOrEmail: z.union([EmailSchema, UsernameSchema]),
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

const AuthenticationOptionsSchema = z.object({
	options: z.object({ challenge: z.string() }),
}) satisfies z.ZodType<{ options: PublicKeyCredentialRequestOptionsJSON }>

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	return {}
}

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	await checkHoneypot(formData)

	// TODO: move check for password to here
	const userNameOrEmail = formData.get('usernameOrEmail') as unknown as string
	const userWithoutPassword = await prisma.user.findFirst({
		where: {
			AND: [
				{ password: null },
				{
					OR: [
						{ email: userNameOrEmail },
						{
							username: userNameOrEmail,
						},
					],
				},
			],
		},
	})

	if (userWithoutPassword) {
		const { verifyUrl, redirectTo, otp } = await prepareVerification({
			period: 10 * 60,
			request,
			type: userWithoutPassword.imported
				? 'set-password-imported'
				: 'set-password',
			target: userWithoutPassword.email,
		})

		const response = await sendEmail({
			to: userWithoutPassword.email,
			subject: `Publicare Akademie - Bestätigung der E-Mail Adresse`,
			react: (
				<ConfirmationEmailAccountEmail
					onboardingUrl={verifyUrl.toString()}
					otp={otp}
				/>
			),
		})

		if (response.status === 'success') {
			return redirect(redirectTo.toString())
		}
	}

	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const session = await login({ ...data, ctx })
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Ungültiger Benutzername oder Passwort',
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session, remember, redirectTo } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

export default function LoginPage({ actionData }: Route.ComponentProps) {
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	return (
		<main className="font-poppins mx-auto grid h-full max-w-6xl place-items-center">
			<img src={bannerImage} alt="Banner" className="hidden sm:block" />

			<div className="container my-8 flex flex-col gap-8 md:my-24 lg:flex-row">
				<div>
					<h1 className="text-pcteal-default md:text-h1 text-h2 font-alumni pb-4 pl-[50px] indent-[-50px] font-normal md:pb-16">
						Kontinuierliche{' '}
						<strong className="font-semibold">Fortbildung</strong> <br />
						mit der publicare{' '}
						<strong className="font-semibold">Akademie</strong>
					</h1>
					<p className="pb-8 text-lg">
						Die medizinische und pharmazeutische Wissenschaft entwickelt sich
						rasant weiter. Für Fachpersonen im Gesundheitsbereich ist es daher
						unerlässlich, ihr Wissen regelmäßig zu aktualisieren und auszubauen.
						Nur so kann eine qualitativ hochwertige Betreuung und Beratung auf
						dem neuesten Stand der Forschung gewährleistet werden.
					</p>
					<p className="text-lg">
						Das umfassende Fortbildungsangebot der publicare steht registrierten
						Nutzerinnen und Nutzern nach dem Login zur Verfügung.
					</p>
				</div>
				<div className="border-pcteal-default flex min-w-[320px] flex-col gap-2 rounded border-2 p-4">
					<LoginForm actionData={actionData} />
					<p className="font-alumni text-center text-xl">Noch kein Konto?</p>
					<Button
						asChild
						className="bg-pcblue-default font-alumni w-full text-2xl uppercase"
					>
						<Link
							to={
								redirectTo
									? `/registrieren?redirectTo=${encodeURIComponent(redirectTo)}`
									: '/registrieren'
							}
						>
							Registrieren
						</Link>
					</Button>
				</div>
			</div>
		</main>
	)
}

function LoginForm({
	actionData,
}: {
	actionData?: { result: SubmissionResult<string[]> }
}) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<Form method="POST" {...getFormProps(form)}>
			<HoneypotInputs />
			<h2 className="font-alumni text-h2 text-pcteal-default mb-4 leading-8 font-semibold">
				Anmeldung
			</h2>
			<Field
				labelProps={{
					children: 'Benutzername oder Email',
					className: 'font-alumni text-lg',
				}}
				inputProps={{
					...getInputProps(fields.usernameOrEmail, { type: 'text' }),
					autoFocus: true,
					className: 'lowercase',
					autoComplete: 'username',
				}}
				errors={fields.usernameOrEmail.errors}
			/>

			<Field
				labelProps={{ children: 'Passwort', className: 'font-alumni text-lg' }}
				inputProps={{
					...getInputProps(fields.password, {
						type: 'password',
					}),
					autoComplete: 'current-password',
				}}
				errors={fields.password.errors}
			/>

			<div className="flex justify-between">
				<CheckboxField
					labelProps={{
						htmlFor: fields.remember.id,
						children: 'Login merken',
					}}
					buttonProps={getInputProps(fields.remember, {
						type: 'checkbox',
					})}
					errors={fields.remember.errors}
					className={'hidden'}
				/>
				<div>
					<Link
						to="/forgot-password"
						className="font-alumni text-xl font-normal"
					>
						Anmeldedaten vergessen?
					</Link>
				</div>
			</div>

			<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
			<ErrorList errors={form.errors} id={form.errorId} />

			<div className="flex items-center justify-between gap-6 pt-3">
				<StatusButton
					className="bg-pcteal-default font-alumni w-full text-2xl uppercase"
					status={isPending ? 'pending' : (form.status ?? 'idle')}
					type="submit"
					disabled={isPending}
				>
					Anmelden
				</StatusButton>
			</div>
		</Form>
	)
}

const VerificationResponseSchema = z.discriminatedUnion('status', [
	z.object({
		status: z.literal('success'),
		location: z.string(),
	}),
	z.object({
		status: z.literal('error'),
		error: z.string(),
	}),
])

function PasskeyLogin({
	redirectTo,
	remember,
}: {
	redirectTo: string | null
	remember: boolean
}) {
	const [isPending] = useTransition()
	const [error, setError] = useState<string | null>(null)
	const [passkeyMessage, setPasskeyMessage] = useOptimistic<string | null>(
		'Login with a passkey',
	)
	const navigate = useNavigate()

	async function handlePasskeyLogin() {
		try {
			setPasskeyMessage('Generating Authentication Options')
			// Get authentication options from the server
			const optionsResponse = await fetch('/webauthn/authentication')
			const json = await optionsResponse.json()
			const { options } = AuthenticationOptionsSchema.parse(json)

			setPasskeyMessage('Requesting your authorization')
			const authResponse = await startAuthentication({ optionsJSON: options })
			setPasskeyMessage('Verifying your passkey')

			// Verify the authentication with the server
			const verificationResponse = await fetch('/webauthn/authentication', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ authResponse, remember, redirectTo }),
			})

			const verificationJson = await verificationResponse.json().catch(() => ({
				status: 'error',
				error: 'Unknown error',
			}))

			const parsedResult =
				VerificationResponseSchema.safeParse(verificationJson)
			if (!parsedResult.success) {
				throw new Error(parsedResult.error.message)
			} else if (parsedResult.data.status === 'error') {
				throw new Error(parsedResult.data.error)
			}
			const { location } = parsedResult.data

			setPasskeyMessage("You're logged in! Navigating...")
			await navigate(location ?? '/')
		} catch (e) {
			const errorMessage = getErrorMessage(e)
			setError(`Failed to authenticate with passkey: ${errorMessage}`)
		}
	}

	return (
		<form action={handlePasskeyLogin}>
			<StatusButton
				id="passkey-login-button"
				aria-describedby="passkey-login-button-error"
				className="w-full"
				status={isPending ? 'pending' : error ? 'error' : 'idle'}
				type="submit"
				disabled={isPending}
			>
				<span className="inline-flex items-center gap-1.5">
					<Icon name="passkey" />
					<span>{passkeyMessage}</span>
				</span>
			</StatusButton>
			<div className="mt-2">
				<ErrorList errors={[error]} id="passkey-login-button-error" />
			</div>
		</form>
	)
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Anmeldung Publicare Academy' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
